import { TFunction } from "i18next";
import _get from "lodash/get";

import {
  ALL_DATABASES,
  ALL_ITEMS,
  ALL_SCHEMAS_ALL_DATABASES,
  ALL_TABLES_ALL_DATABASES,
  ALL_VIEWS_ALL_DATABASES,
} from "./PrivilegesTable/constant";
import {
  PermissionError,
  PrivilegesState,
  PrivilegesStateWithErrors,
} from "./types";

export const ROLES_WIZARD_INIT_EDIT_DATABASE_PRIVILEGES =
  "ROLES_WIZARD_INIT_EDIT_DATABASE_PRIVILEGES";
export const ROLES_WIZARD_INIT_DATABASE_PRIVILEGES =
  "ROLES_WIZARD_INIT_DATABASE_PRIVILEGES";
export const ROLES_WIZARD_ADD_GROUP_EMPTY = "ROLES_WIZARD_ADD_GROUP_EMPTY";
export const ROLES_WIZARD_ADD_GROUP_WITH_REMAINING_OBJECTS =
  "ROLES_WIZARD_ADD_GROUP_WITH_REMAINING_OBJECTS";
export const ROLES_WIZARD_DELETE_GROUP = "ROLES_WIZARD_DELETE_GROUP";

export const ROLES_WIZARD_SET_RESOURCE_OBJECTS =
  "ROLES_WIZARD_SET_RESOURCE_OBJECTS";

export const ROLES_WIZARD_SET_ASSIGN_PRIVILEGES =
  "ROLES_WIZARD_SET_ASSIGN_PRIVILEGES";
export const ROLES_WIZARD_SET_REVOKE_PRIVILEGES =
  "ROLES_WIZARD_SET_REVOKE_PRIVILEGES";

export const ROLES_WIZARD_TOGGLE_EXPAND_GROUP =
  "ROLES_WIZARD_TOGGLE_EXPAND_GROUP";

export const ROLES_WIZARD_IGNORE_ERROR = "ROLES_WIZARD_IGNORE_ERROR";

export const ROLES_WIZARD_SET_EMPTY_SECTION_ERRORS =
  "ROLES_WIZARD_SET_EMPTY_SECTION_ERRORS";

const setElementAtIndex = (
  array: unknown[],
  element: unknown,
  index: number
) => {
  return [
    ...array.slice(0, index),
    element,
    ...array.slice(index + 1, array.length),
  ];
};

const hasDatabaseUsage = (
  state: PrivilegesStateWithErrors,
  catalogName: string
) => {
  const { database, account } = state;
  for (const accountGroup of account) {
    const { toAssign } = accountGroup;
    if (toAssign.includes("USAGE ANY DATABASE")) {
      return true;
    }
  }
  for (const databaseGroup of database) {
    const { objects, toAssign } = databaseGroup;
    if (
      (objects.includes(catalogName) || objects.includes(ALL_DATABASES)) &&
      toAssign.includes("USAGE")
    ) {
      return true;
    }
  }
  return false;
};

const hasSchemaUsage = (
  state: PrivilegesState,
  schemaName: string,
  catalogName?: string
) => {
  for (const schemaGroup of state.schema) {
    const { objects, toAssign } = schemaGroup;
    const object = objects.find(object => {
      return (
        object.name === schemaName ||
        (catalogName &&
          object.catalogName === catalogName &&
          object.name === schemaName)
      );
    });
    if (toAssign.includes("USAGE") && object) {
      return true;
    }
  }
  return false;
};

export const hasEmptySectionErrors = (errors: Record<string, string[]>) =>
  Object.keys(errors).find(sectionName => {
    const section = errors[sectionName as keyof typeof errors];
    return section.filter(Boolean).length;
  });

export const getEmptySectionsErrors = (
  t: TFunction,
  state: PrivilegesStateWithErrors,
  initialState: PrivilegesState,
  catalogName?: string
): Record<string, string[]> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { permissionErrors, emptySectionErrors, ...sections } = ((catalogName
    ? state.databasePrivileges[catalogName]
    : state) || {}) as PrivilegesStateWithErrors;
  const sectionNames = Object.keys(sections);
  const sectionErrors = sectionNames.reduce<Record<string, string[]>>(
    (acc, sectionName) => {
      acc[sectionName] = [];
      return acc;
    },
    {}
  );
  for (const sectionName of sectionNames) {
    const section = sections[sectionName as keyof PrivilegesState];
    for (let i = 0; i < section.length; i++) {
      const group = section[i];
      const initialPrivileges = _get(
        initialState,
        [sectionName, i, "toAssign"],
        []
      );
      if (group.objects.length === 0 && group.toAssign.length > 0) {
        sectionErrors[sectionName][i] = t(
          "roles_wizard.missing_permission.group_has_privileges"
        );
      }

      if (
        sectionName !== "account" &&
        sectionName !== "organization" &&
        !ALL_ITEMS.includes(group.objects[0] as string) &&
        group.objects.length > 0 &&
        !group.toAssign.length &&
        !initialPrivileges.length
      ) {
        sectionErrors[sectionName][i] = t(
          "roles_wizard.missing_permission.group_has_objects"
        );
      }
    }
  }
  return sectionErrors;
};

const getPermissionErrors = (
  state: PrivilegesStateWithErrors,
  catalogName?: string
) => {
  const { permissionErrors, databasePrivileges } = state;
  const { table, view, schema } = catalogName
    ? databasePrivileges[catalogName]
    : state;

  const viewErrors = permissionErrors.view.filter(error => error.ignored);
  const tableErrors = permissionErrors.table.filter(error => error.ignored);
  const schemaErrors = permissionErrors.schema.filter(error => error.ignored);

  const exists = (
    errors: PermissionError[],
    {
      object,
      catalogName,
      resource,
    }: {
      object: string;
      catalogName?: string;
      resource: string;
    }
  ) => {
    const existingError = errors.find(error => {
      if (catalogName) {
        return (
          error.object === object &&
          error.catalogName === catalogName &&
          error.resource === resource
        );
      }
      return error.object === object && error.resource === resource;
    });

    return existingError;
  };

  const fillBulkSchemaErrors = (
    errors: PermissionError[],
    object: { name: string; catalogName: string },
    actions: string[],
    type: string
  ) => {
    const error = {
      resource: "schema",
      object: ALL_SCHEMAS_ALL_DATABASES,
      action: "USAGE",
    };
    !hasSchemaUsage(
      catalogName ? state.databasePrivileges[catalogName] : state,
      ALL_SCHEMAS_ALL_DATABASES
    ) &&
      !exists(errors, error) &&
      errors.push({
        ...error,
        errorSource: {
          catalogName: object.catalogName,
          type,
          name: object.name,
          actions,
        },
      });
  };

  const fillBulkDatabaseErrors = (
    errors: PermissionError[],
    object: { name: string; catalogName: string },
    actions: string[],
    type: string
  ) => {
    const error = {
      resource: "database",
      object: ALL_DATABASES,
    };
    !hasDatabaseUsage(state, ALL_DATABASES) &&
      !exists(errors, error) &&
      errors.push({
        ...error,
        action: "USAGE",
        errorSource: {
          catalogName: object.catalogName,
          type,
          name: object.name,
          actions,
        },
      });
  };

  const fillSchemaErrors = (
    errors: PermissionError[],
    object: { name: string; catalogName: string },
    actions: string[],
    type: string
  ) => {
    const error = {
      resource: "schema",
      object: "public",
      catalogName,
      action: "USAGE",
    };
    if (
      !hasSchemaUsage(
        catalogName ? state.databasePrivileges[catalogName] : state,
        "public",
        catalogName
      ) &&
      !exists(errors, error)
    ) {
      errors.push({
        ...error,
        errorSource: {
          catalogName: object.catalogName,
          type,
          name: object.name,
          actions,
        },
      });
    }
  };

  const fillDatabaseErrors = (
    errors: PermissionError[],
    { name, catalogName }: { name: string; catalogName: string },
    actions: string[],
    type: string
  ) => {
    const error = {
      resource: "database",
      object: catalogName,
      action: "USAGE",
    };
    if (!hasDatabaseUsage(state, catalogName) && !exists(errors, error)) {
      errors.push({
        ...error,
        errorSource: {
          catalogName,
          type,
          name,
          actions,
        },
      });
    }
  };

  for (const viewGroup of view) {
    const { objects, toAssign } = viewGroup;
    if (toAssign.length) {
      for (const view of objects) {
        if (view.name === ALL_VIEWS_ALL_DATABASES) {
          fillBulkDatabaseErrors(viewErrors, view, toAssign, "view");
          fillBulkSchemaErrors(viewErrors, view, toAssign, "view");
        } else {
          fillSchemaErrors(viewErrors, view, toAssign, "view");
          fillDatabaseErrors(viewErrors, view, toAssign, "view");
        }
      }
    }
  }

  for (const tableGroup of table) {
    const { objects, toAssign } = tableGroup;
    if (toAssign.length) {
      for (const table of objects) {
        if (table.name === ALL_TABLES_ALL_DATABASES) {
          fillBulkDatabaseErrors(tableErrors, table, toAssign, "table");
          fillBulkSchemaErrors(tableErrors, table, toAssign, "table");
        } else {
          fillSchemaErrors(tableErrors, table, toAssign, "table");
          fillDatabaseErrors(tableErrors, table, toAssign, "table");
        }
      }
    }
  }

  for (const schemaGroup of schema) {
    const { objects, toAssign } = schemaGroup;
    if (toAssign.length) {
      for (const schema of objects) {
        if (schema.name === ALL_SCHEMAS_ALL_DATABASES) {
          fillBulkDatabaseErrors(schemaErrors, schema, toAssign, "schema");
        } else {
          fillDatabaseErrors(schemaErrors, schema, toAssign, "schema");
        }
      }
    }
  }

  return {
    view: viewErrors,
    table: tableErrors,
    schema: schemaErrors,
  };
};

export const privilegesReducer = (
  state: PrivilegesStateWithErrors,
  action: any
) => {
  switch (action.type) {
    case ROLES_WIZARD_INIT_EDIT_DATABASE_PRIVILEGES: {
      const { catalogName } = action;
      const permissionErrors = getPermissionErrors(state, catalogName);
      return {
        ...state,
        permissionErrors,
      };
    }
    case ROLES_WIZARD_INIT_DATABASE_PRIVILEGES: {
      const { catalogName, schema, table, view } = action;
      const privileges = {
        view,
        schema,
        table,
      };

      const newState = {
        ...state,
        databasePrivileges: {
          ...state.databasePrivileges,
          [catalogName]: {
            ...privileges,
            initialPrivileges: privileges,
          },
        },
      };

      const permissionErrors = getPermissionErrors(newState, catalogName);
      return {
        ...newState,
        permissionErrors,
      };
    }
    case ROLES_WIZARD_ADD_GROUP_EMPTY: {
      const { resource, objects = [], catalogName } = action;
      const resourceGroups = catalogName
        ? state.databasePrivileges[catalogName][
            resource as keyof PrivilegesState
          ]
        : state[resource as keyof PrivilegesState];
      const newGroup = {
        objects,
        toAssign: [],
        toDeny: [],
      };
      const newState = catalogName
        ? {
            ...state,
            databasePrivileges: {
              ...state.databasePrivileges,
              [catalogName]: {
                ...(state.databasePrivileges[catalogName] || {}),
                [resource]: [...resourceGroups, newGroup],
              },
            },
          }
        : {
            ...state,
            [resource]: [...resourceGroups, newGroup],
          };
      return newState;
    }
    case ROLES_WIZARD_ADD_GROUP_WITH_REMAINING_OBJECTS: {
      const { resource, allObjects, catalogName } = action as {
        catalogName?: string;
        resource: string;
        allObjects: string[];
      };
      const resourceGroups = catalogName
        ? state.databasePrivileges[catalogName][
            resource as keyof PrivilegesState
          ]
        : state[resource as keyof PrivilegesState];
      const affectedObjects = new Set();

      for (const group of resourceGroups) {
        const { objects } = group;
        objects.forEach(object => affectedObjects.add(object));
      }
      const remainingObjects = allObjects.filter(object => {
        return !affectedObjects.has(object);
      });
      const newState = catalogName
        ? {
            ...state,
            databasePrivileges: {
              ...state.databasePrivileges,
              [catalogName]: {
                ...(state.databasePrivileges[catalogName] || {}),
                [resource]: [
                  ...resourceGroups,
                  { objects: remainingObjects, toAssign: [], toDeny: [] },
                ],
              },
            },
          }
        : {
            ...state,
            [resource]: [
              ...resourceGroups,
              { objects: remainingObjects, toAssign: [], toDeny: [] },
            ],
          };
      return newState;
    }
    case ROLES_WIZARD_DELETE_GROUP: {
      const { resource, index, catalogName } = action;
      const resourceGroups = catalogName
        ? state.databasePrivileges[catalogName][
            resource as keyof PrivilegesState
          ]
        : state[resource as keyof PrivilegesState];
      const newGroups = [
        ...resourceGroups.slice(0, index),
        ...resourceGroups.slice(index + 1, resourceGroups.length),
      ];
      const newState = catalogName
        ? {
            ...state,
            databasePrivileges: {
              ...(state.databasePrivileges || {}),
              [catalogName]: {
                ...(state.databasePrivileges[catalogName] || {}),
                [resource]: newGroups,
              },
            },
          }
        : {
            ...state,
            [resource]: newGroups,
          };
      const permissionErrors = getPermissionErrors(newState, catalogName);
      const removedGroup = resourceGroups[index];
      const { objects } = removedGroup;
      let filteredErrors = permissionErrors;
      for (const object of objects) {
        const resourceErrors =
          filteredErrors[resource as keyof typeof filteredErrors] || [];

        filteredErrors = {
          ...filteredErrors,
          [resource]: resourceErrors.filter(error => {
            return typeof object === "string"
              ? false
              : error.errorSource.name !== object.name;
          }),
        };
      }

      return {
        ...newState,
        permissionErrors: filteredErrors,
      };
    }
    case ROLES_WIZARD_SET_RESOURCE_OBJECTS: {
      const { resource, objects, index, catalogName } = action;
      const groups = catalogName
        ? state.databasePrivileges[catalogName]?.[
            resource as keyof PrivilegesState
          ]
        : state[resource as keyof PrivilegesState];
      const resourceGroup = groups[index];

      const group = {
        ...resourceGroup,
        objects,
      };

      const newGroups = setElementAtIndex(groups, group, index);

      const newState = catalogName
        ? {
            ...state,
            databasePrivileges: {
              ...(state.databasePrivileges || {}),
              [catalogName]: {
                ...(state.databasePrivileges[catalogName] || {}),
                [resource]: newGroups,
              },
            },
          }
        : {
            ...state,
            [resource]: newGroups,
          };

      const permissionErrors = getPermissionErrors(newState, catalogName);
      const newEmptySectionErrors = {
        ...state.emptySectionErrors,
        [resource]: (state.emptySectionErrors?.[resource] || []).map(
          (value: string, i: number) => (i === index ? null : value)
        ),
      };

      return {
        ...newState,
        permissionErrors,
        emptySectionErrors: newEmptySectionErrors,
      };
    }
    case ROLES_WIZARD_SET_ASSIGN_PRIVILEGES: {
      const { resource, privileges, index, catalogName } = action;
      const groups = catalogName
        ? state.databasePrivileges?.[catalogName][
            resource as keyof PrivilegesState
          ]
        : state[resource as keyof PrivilegesState];
      const resourceGroup = groups[index];

      const group = {
        ...resourceGroup,
        toAssign: privileges,
      };

      const newGroups = setElementAtIndex(groups, group, index);
      const newState = catalogName
        ? {
            ...state,
            databasePrivileges: {
              ...(state.databasePrivileges || {}),
              [catalogName]: {
                ...(state.databasePrivileges[catalogName] || {}),
                [resource]: newGroups,
              },
            },
          }
        : {
            ...state,
            [resource]: newGroups,
          };

      const permissionErrors = getPermissionErrors(newState, catalogName);

      const newEmptySectionErrors = {
        ...state.emptySectionErrors,
        [resource]: (state.emptySectionErrors?.[resource] || []).map(
          (value: any, i: number) => (i === index ? null : value)
        ),
      };

      return {
        ...newState,
        permissionErrors,
        emptySectionErrors: newEmptySectionErrors,
      };
    }
    case ROLES_WIZARD_SET_REVOKE_PRIVILEGES: {
      const { resource, privileges, index } = action;
      const groups = state[resource as keyof PrivilegesState];
      const resourceGroup = groups[index];

      const group = {
        ...resourceGroup,
        toDeny: privileges,
      };

      const newGroups = setElementAtIndex(groups, group, index);
      const newState = {
        ...state,
        [resource]: newGroups,
      };

      const permissionErrors = getPermissionErrors(newState);
      return {
        ...newState,
        permissionErrors,
      };
    }
    case ROLES_WIZARD_TOGGLE_EXPAND_GROUP: {
      const { index, resource, catalogName } = action;
      const groups = catalogName
        ? state.databasePrivileges[catalogName][
            resource as keyof PrivilegesState
          ]
        : state[resource as keyof PrivilegesState];
      const resourceGroup = groups[index];
      const group = {
        ...resourceGroup,
        expanded: !resourceGroup.expanded,
      };
      const newGroups = setElementAtIndex(groups, group, index);
      const newState = catalogName
        ? {
            ...state,
            databasePrivileges: {
              ...(state.databasePrivileges || {}),
              [catalogName]: {
                ...(state.databasePrivileges[catalogName] || {}),
                [resource]: newGroups,
              },
            },
          }
        : {
            ...state,
            [resource]: newGroups,
          };
      return newState;
    }
    case ROLES_WIZARD_IGNORE_ERROR: {
      const { resource, error } = action;
      const { permissionErrors } = state;
      const currentResoruceErrors =
        permissionErrors[resource as keyof typeof permissionErrors];
      const resourceErrors = currentResoruceErrors.map(currentError => {
        if (
          currentError.resource === error.resource &&
          currentError.object === error.object &&
          currentError.catalogName === error.catalogName
        ) {
          return { ...currentError, ignored: true };
        }
        return currentError;
      });
      return {
        ...state,
        permissionErrors: {
          ...permissionErrors,
          [resource]: resourceErrors,
        },
      };
    }
    case ROLES_WIZARD_SET_EMPTY_SECTION_ERRORS: {
      const { errors } = action;
      return {
        ...state,
        emptySectionErrors: errors,
      };
    }
    default: {
      return state;
    }
  }
};
